import { isIOS } from '@e3dc-react/shell/Configuration'
import { CircularProgress, Slider, Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React, { SyntheticEvent, useState } from 'react'
import { MINIMUM_EVSE_LED_INTENSITY, MAXIMUM_EVSE_LED_INTENSITY } from 'src/Libs/flow/constants'
import { syncInstallation } from 'src/Libs/flow/installation/api'
import { ModbusTcpChargingStation } from 'src/Libs/flow/installation/forms'
import { installationDevicesKey } from 'src/Libs/flow/installation/hooks'
import { getModbusTcpPUTDto } from 'src/Libs/flow/parsers'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { SettingsBox } from 'src/Elements/Data'
import { Box } from '@mui/system'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { updateEvse } from '../api'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useAppRatingContext } from 'src/Libs/app-rating/hooks'
import { usePortalMutation } from 'src/Libs/api'

interface LedIntensityProps {
  installationId: number
  queryIsLoading: boolean
  evse: ModbusTcpChargingStation
}

/** The LED intensity page of an installation's (user-level) charging station settings. */
export const LedIntensity: React.FC<LedIntensityProps> = ({ installationId, queryIsLoading, evse }) => {
  const { t } = useCustomTranslation()
  const { triggerAppRating } = useAppRatingContext()

  const { enqueueErrorStack } = useSnackbar()
  const { hasInstallationWritingRights: userCanEdit } = useInstallationAccessRightsContext()

  const currentLedIntensity = evse.ledIntensityPercentage || 0

  /** Value to control the slider. Updates when the slider is moved or let go. */
  const [ledIntensity, setLedIntensity] = useState(currentLedIntensity)

  const handleLedIntensityChange = (event: Event | SyntheticEvent<Element, Event>, newValue: number | number[]): void => {
    /** Slider component workaround for Safari https://github.com/mui/material-ui/issues/31869 */
    if (event.type === 'mousedown' && isIOS) {
      return
    }

    if (typeof newValue === 'number') {
      setLedIntensity(newValue)
    }
  }

  const authFetch = useAuthFetchWithResult()
  const queryClient = useQueryClient()
  const mutateLedIntensity = usePortalMutation({
    mutationFn: (newLedIntensity: number) =>
      updateEvse(
        installationId,
        evse.rawData?.deviceLinkId?.toString() ?? '',
        evse.rawData?.id?.toString() ?? '',
        getModbusTcpPUTDto({ ...evse, ledIntensityPercentage: newLedIntensity }),
        authFetch
      ).then(() => syncInstallation(installationId, authFetch)),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: installationDevicesKey(installationId),
      }),
    onError: () => enqueueErrorStack(t('common.update-failed-error')),
  })

  const handleCommitedLedIntensityChange = (event: Event | SyntheticEvent<Element, Event>, newValue: number | number[]): void => {
    /** Slider component workaround for Safari https://github.com/mui/material-ui/issues/31869 */
    if (event.type === 'mousedown' && isIOS) {
      return
    }

    if (typeof newValue === 'number' && newValue !== currentLedIntensity && !mutateLedIntensity.isPending) {
      setLedIntensity(newValue)
      mutateLedIntensity.mutate(newValue)
    }
    triggerAppRating()
  }

  const isLoading = queryIsLoading || mutateLedIntensity.isPending

  return (
    <AccordionItem title={t('user-settings.evse-page.led-intensity')} expanded accordionDetailsSx={{ px: 4, py: 2 }}>
      <Stack gap={2}>
        <SettingsBox
          title={t('user-settings.evse-led-page.title')}
          suffix={
            <Box sx={{ minWidth: { xs: '100%', sm: '50%' } }}>
              <Slider
                valueLabelDisplay="on"
                aria-label={ledIntensity.toString()}
                disabled={!userCanEdit}
                min={MINIMUM_EVSE_LED_INTENSITY}
                max={MAXIMUM_EVSE_LED_INTENSITY}
                value={ledIntensity || 0}
                onChange={handleLedIntensityChange}
                onChangeCommitted={handleCommitedLedIntensityChange}
                sx={{
                  height: 18,
                  padding: '13px 0',

                  '& .MuiSlider-track': {
                    height: 18,
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: 'primary.main',
                    boxShadow: 'none',
                  },

                  '& .MuiSlider-rail': {
                    height: 18,
                    borderRadius: '12px',
                    backgroundColor: 'background.paper',
                    opacity: 1,
                  },

                  '& .MuiSlider-thumb': {
                    height: 20,
                    width: 20,
                    backgroundColor: 'background.paper',
                    border: `2px solid primary.main`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },

                  '& .MuiSlider-valueLabel': {
                    backgroundColor: 'transparent',
                    color: 'text.primary',
                    fontSize: 12,
                    fontWeight: 400,
                    top: 0,
                    padding: 0,

                    '&::before': {
                      display: 'none',
                    },
                  },
                  '&.Mui-disabled': {
                    '& .MuiSlider-track': {
                      backgroundColor: 'action.disabled',
                    },
                    '& .MuiSlider-thumb': {
                      backgroundColor: 'grey.grey05',
                    },
                  },
                }}
              />
            </Box>
          }
        />

        {isLoading && <CircularProgress sx={{ mx: 'auto' }} />}
      </Stack>
    </AccordionItem>
  )
}
